import { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Hospital, CheckCircle, Users, HeartHandshake, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HospitalPartnersPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const benefits = [
    {
      icon: Users,
      title: "Enhanced Patient Experience",
      description: "Offer families of your patients access to comfortable lodging, reducing stress during difficult times."
    },
    {
      icon: HeartHandshake,
      title: "Family Support Services",
      description: "Connect families with transportation and daily necessities through our network of partners."
    },
    {
      icon: Hospital,
      title: "Community Partnership",
      description: "Strengthen your hospital's community presence by partnering with a local nonprofit."
    }
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/partners/hospitals`, {
        hospital_name: data.hospitalName,
        contact_name: data.contactName,
        email: data.email,
        phone: data.phone,
        city: data.city,
        state: data.state,
        department: data.department || null,
        message: data.message
      });
      
      toast.success("Thank you! Your inquiry has been submitted successfully.");
      setSubmitted(true);
      reset();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1717497932377-7758b8d5b45e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGV4dGVyaW9yJTIwZnJpZW5kbHklMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzcwMzQ3NTY4fDA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl space-y-6 animate-fade-in-up">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Hospital Partners</span>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
              Support Your Patients' <span className="text-accent">Families</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed">
              Join our network of hospitals committed to providing comprehensive care. 
              Help connect your patients' families with lodging, transportation, and support services.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 lg:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Partnership Benefits</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              Why Hospitals Partner With Us
            </h2>
            <p className="text-muted-foreground mt-4">
              Enhance your patient care services by connecting families with the support they need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className="bg-card border-border/50 card-hover"
                data-testid={`hospital-benefit-card-${index}`}
              >
                <CardContent className="p-8 space-y-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                    <benefit.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-foreground">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">The Process</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              How It Works for Hospitals
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Connect", desc: "Submit your inquiry through our form" },
              { step: "2", title: "Coordinate", desc: "We'll meet to understand your needs" },
              { step: "3", title: "Integrate", desc: "Set up referral processes with your team" },
              { step: "4", title: "Support", desc: "Start connecting families with resources" }
            ].map((item, index) => (
              <div key={index} className="text-center relative" data-testid={`hospital-process-step-${index}`}>
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground font-serif text-2xl font-bold flex items-center justify-center mx-auto">
                  {item.step}
                </div>
                {index < 3 && (
                  <ArrowRight className="hidden md:block absolute top-8 right-0 transform translate-x-1/2 w-6 h-6 text-muted-foreground" />
                )}
                <h3 className="font-serif text-lg font-bold text-foreground mt-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm mt-2">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 lg:py-32 bg-secondary" id="contact-form">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {submitted ? (
            <Card className="bg-card border-border/50" data-testid="hospital-submission-success">
              <CardContent className="p-12 text-center space-y-4">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-primary" />
                </div>
                <h2 className="font-serif text-2xl font-bold text-foreground">
                  Thank You!
                </h2>
                <p className="text-muted-foreground">
                  Your inquiry has been received. Our team will contact you within 2-3 business days 
                  to discuss how we can support your patients' families.
                </p>
                <Button 
                  onClick={() => setSubmitted(false)}
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground mt-4"
                >
                  Submit Another Inquiry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border/50">
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-serif text-2xl">Join Our Network</CardTitle>
                <CardDescription>
                  Fill out the form below and our team will reach out to discuss how we can work together.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="hospital-partner-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="hospitalName">Hospital Name *</Label>
                      <Input 
                        id="hospitalName"
                        {...register("hospitalName", { required: "Hospital name is required" })}
                        placeholder="General Hospital"
                        className="input-focus"
                        data-testid="hospital-name-input"
                      />
                      {errors.hospitalName && (
                        <p className="text-destructive text-sm">{errors.hospitalName.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <Input 
                        id="contactName"
                        {...register("contactName", { required: "Contact name is required" })}
                        placeholder="Dr. Jane Doe"
                        className="input-focus"
                        data-testid="hospital-contact-name-input"
                      />
                      {errors.contactName && (
                        <p className="text-destructive text-sm">{errors.contactName.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        {...register("email", { 
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                        placeholder="jane@hospital.org"
                        className="input-focus"
                        data-testid="hospital-email-input"
                      />
                      {errors.email && (
                        <p className="text-destructive text-sm">{errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input 
                        id="phone"
                        {...register("phone", { required: "Phone is required" })}
                        placeholder="(555) 123-4567"
                        className="input-focus"
                        data-testid="hospital-phone-input"
                      />
                      {errors.phone && (
                        <p className="text-destructive text-sm">{errors.phone.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input 
                        id="city"
                        {...register("city", { required: "City is required" })}
                        placeholder="Anytown"
                        className="input-focus"
                        data-testid="hospital-city-input"
                      />
                      {errors.city && (
                        <p className="text-destructive text-sm">{errors.city.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Input 
                        id="state"
                        {...register("state", { required: "State is required" })}
                        placeholder="CA"
                        className="input-focus"
                        data-testid="hospital-state-input"
                      />
                      {errors.state && (
                        <p className="text-destructive text-sm">{errors.state.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="department">Department (Optional)</Label>
                    <Input 
                      id="department"
                      {...register("department")}
                      placeholder="Patient Services, Social Work, etc."
                      className="input-focus"
                      data-testid="hospital-department-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Additional Message (Optional)</Label>
                    <Textarea 
                      id="message"
                      {...register("message")}
                      placeholder="Tell us about your hospital and how you'd like to work together..."
                      rows={4}
                      className="input-focus resize-none"
                      data-testid="hospital-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 btn-hover"
                    data-testid="submit-hospital-form"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Partnership Inquiry"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}
